// Boomin' — Active (coupon-in-use) screen (/active/:id)
// 30-second countdown, confetti, animated shimmer for anti-fraud.

import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useHistory, useParams } from "react-router-dom";
import {
  BRAND,
  ACCENT,
  PictX,
  ConfettiBurst,
  shadeMix,
  useBoominFx,
} from "./shared";
import {
  RedesignCategory,
  categoryPict,
  consumeCoupon,
  fetchCoupon,
  inferCategory,
  isUserLoggedIn,
} from "./api";
import { Coupon, Hub } from "../../Types/App";

const brand = BRAND;
const accent = ACCENT;

const TOTAL_SECS = 30;

export const ActiveScreen: React.FC = () => {
  useBoominFx();
  const history = useHistory();
  const { id } = useParams<{ id?: string }>();

  const [coupon, setCoupon] = useState<Coupon | null>(null);
  const [hub, setHub] = useState<Hub | null>(null);
  const [statusError, setStatusError] = useState<string>("");
  const [ready, setReady] = useState(false);
  const [secs, setSecs] = useState(TOTAL_SECS);
  const usedRef = useRef<boolean>(false);

  // Handle empty route — redesign always navigates with an id, but guard the
  // bare /active route in case someone deep-links to it.
  useEffect(() => {
    if (!id) {
      setStatusError("クーポンが指定されていません");
      setReady(true);
    }
  }, [id]);

  // If the user isn't logged in we can't call the use endpoint; bounce to login.
  useEffect(() => {
    if (!isUserLoggedIn() && id) {
      try {
        sessionStorage.setItem("boomin_return_to", `/active/${id}`);
      } catch {
        /* ignore */
      }
      window.location.replace("/public/oauth/instabase");
    }
  }, [id]);

  // Fetch coupon details and, if it's still available, mark it used exactly once.
  useEffect(() => {
    let cancelled = false;
    if (!id || !isUserLoggedIn()) return;
    (async () => {
      try {
        const res = await fetchCoupon(id);
        if (cancelled) return;
        setCoupon(res.coupon);
        setHub(res.hub);
        if (!res.available) {
          setStatusError("このクーポンは既に使用済みです");
          setReady(true);
          return;
        }
        if (!usedRef.current) {
          usedRef.current = true;
          try {
            await consumeCoupon(res.coupon.id);
          } catch (e: any) {
            if (cancelled) return;
            // 既に使用済みケース等
            setStatusError(
              typeof e === "string"
                ? e
                : e?.message || "クーポンを使用できませんでした"
            );
            setReady(true);
            return;
          }
        }
        if (cancelled) return;
        setReady(true);
      } catch (e: any) {
        if (cancelled) return;
        setStatusError(
          typeof e === "string" ? e : e?.message || "クーポン情報を取得できませんでした"
        );
        setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (!ready || statusError) return;
    if (secs <= 0) return;
    const t = setTimeout(() => setSecs((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secs, ready, statusError]);

  const pct = secs / TOTAL_SECS;
  const R = 72;
  const C = 2 * Math.PI * R;

  const cat: RedesignCategory = coupon ? inferCategory(coupon, hub) : "shop";

  return (
    <>
      <Helmet>
        <title>クーポン使用中 | Boomin'</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: `linear-gradient(170deg, ${brand} 0%, ${shadeMix(
            accent,
            brand,
            0.4
          )} 60%, ${accent} 100%)`,
          overflow: "hidden",
          color: "#fff",
          maxWidth: 640,
          margin: "0 auto",
        }}
      >
        <div
          data-boomin-anim
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "radial-gradient(circle, rgba(255,255,255,0.18) 1.6px, transparent 1.6px)",
            backgroundSize: "22px 22px",
            animation: "boom-bg-pan 11s linear infinite",
            zIndex: 0,
          }}
        />

        <ConfettiBurst
          density={40}
          palette={["#FFE05A", "#FFFFFF", "#FFC9B0", "#3DBED9", "#0FAC6D", "#FF9966"]}
        />

        <div
          style={{
            position: "absolute",
            top: "calc(env(safe-area-inset-top, 0px) + 24px)",
            left: 16,
            right: 16,
            zIndex: 6,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <button
            onClick={() => history.goBack()}
            aria-label="閉じる"
            style={{
              width: 36,
              height: 36,
              borderRadius: 999,
              background: "rgba(255,255,255,0.22)",
              border: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
            }}
          >
            <PictX name="close" size={16} stroke="#fff" strokeWidth={2.4} />
          </button>
          <div
            style={{
              fontSize: 10,
              fontWeight: 900,
              color: "#fff",
              letterSpacing: "0.2em",
              background: "rgba(255,255,255,0.18)",
              padding: "5px 12px",
              borderRadius: 999,
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
            }}
          >
            BOOMIN' USE
          </div>
          <div style={{ width: 36 }} />
        </div>

        {/* Hero ticket */}
        <div
          data-boomin-anim
          style={{
            position: "absolute",
            top: "calc(env(safe-area-inset-top, 0px) + 80px)",
            left: 24,
            right: 24,
            zIndex: 5,
            animation: "boom-fly-up 520ms ease-out",
          }}
        >
          <ActiveTicket coupon={coupon} hub={hub} cat={cat} />
        </div>

        {/* Countdown or error */}
        {statusError ? (
          <ErrorPanel message={statusError} onClose={() => history.replace("/discover")} />
        ) : (
          <div
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              top: "calc(env(safe-area-inset-top, 0px) + 280px)",
              zIndex: 5,
              textAlign: "center",
              color: "#fff",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 800,
                opacity: 0.85,
                letterSpacing: "0.2em",
                marginBottom: 12,
              }}
            >
              USE WITHIN
            </div>

            <div
              style={{
                position: "relative",
                width: 168,
                height: 168,
                margin: "0 auto",
              }}
            >
              <svg
                width="168"
                height="168"
                viewBox="0 0 168 168"
                style={{ position: "absolute", inset: 0 }}
              >
                <circle
                  cx="84"
                  cy="84"
                  r={R}
                  stroke="rgba(255,255,255,0.22)"
                  strokeWidth="9"
                  fill="none"
                />
                <circle
                  cx="84"
                  cy="84"
                  r={R}
                  stroke="#fff"
                  strokeWidth="9"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={C}
                  strokeDashoffset={(1 - pct) * C}
                  transform="rotate(-90 84 84)"
                  style={{ transition: "stroke-dashoffset 0.9s linear" }}
                />
              </svg>
              <div
                data-boomin-anim
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  animation: ready ? "boom-tick 1s ease-in-out infinite" : "none",
                }}
              >
                <div
                  style={{
                    fontSize: 68,
                    fontWeight: 800,
                    lineHeight: 1,
                    fontFamily: '"SF Pro Display", -apple-system, system-ui, sans-serif',
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {ready ? secs : "…"}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    opacity: 0.85,
                    marginTop: 2,
                    letterSpacing: "0.1em",
                  }}
                >
                  SECONDS
                </div>
              </div>
            </div>

            <div style={{ marginTop: 22, fontSize: 15, fontWeight: 800 }}>
              店員さんにこの画面をお見せください
            </div>
            <div
              style={{
                marginTop: 6,
                fontSize: 11,
                opacity: 0.78,
                padding: "0 24px",
                lineHeight: 1.6,
              }}
            >
              画面のアニメーションが本物のしるしです
              <br />
              スクリーンショットや画像は無効です
            </div>
          </div>
        )}

        {/* Anti-fraud shimmer */}
        <div
          style={{
            position: "absolute",
            left: 24,
            right: 24,
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 44px)",
            zIndex: 5,
            height: 10,
            borderRadius: 999,
            overflow: "hidden",
            background: "rgba(255,255,255,0.18)",
          }}
        >
          <div
            data-boomin-anim
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.9) 50%, rgba(255,255,255,0) 100%)",
              backgroundSize: "200% 100%",
              animation: "boom-shimmer 1.8s linear infinite",
            }}
          />
        </div>

        {/* Footer timestamp */}
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
            zIndex: 5,
            textAlign: "center",
            color: "rgba(255,255,255,0.75)",
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.12em",
            fontFamily: '"SF Mono", ui-monospace, monospace',
          }}
        >
          {new Date().toISOString().replace("T", " ").slice(0, 19)} · BOOMIN' #{id || "—"}
        </div>
      </div>
    </>
  );
};

const ActiveTicket: React.FC<{
  coupon: Coupon | null;
  hub: Hub | null;
  cat: RedesignCategory;
}> = ({ coupon, hub, cat }) => (
  <div
    style={{
      background: "#fff",
      borderRadius: 22,
      padding: 18,
      boxShadow: "0 22px 44px rgba(0,0,0,0.28), 0 2px 0 rgba(255,255,255,0.5) inset",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
      <div
        data-boomin-anim
        style={{
          position: "relative",
          width: 56,
          height: 56,
          flexShrink: 0,
          borderRadius: 999,
          background: "#FFF1E8",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          animation: "boom-wiggle 1.6s ease-in-out infinite",
        }}
      >
        <PictX name={categoryPict(cat)} size={28} stroke={brand} strokeWidth={1.8} />
        <div
          style={{
            position: "absolute",
            top: -6,
            right: -6,
            background: brand,
            color: "#fff",
            fontSize: 9,
            fontWeight: 900,
            padding: "3px 7px",
            borderRadius: 999,
            letterSpacing: "0.04em",
            boxShadow: `0 3px 8px ${brand}88`,
          }}
        >
          使用中
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 10,
            color: "#999",
            fontWeight: 700,
            letterSpacing: "0.08em",
            marginBottom: 3,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {hub ? `${hub.name}${hub.state ? ` · ${hub.state}` : ""}` : "クーポン読み込み中…"}
        </div>
        <div
          style={{
            fontSize: 20,
            fontWeight: 800,
            lineHeight: 1.25,
            color: "#1A1A1A",
            wordBreak: "break-word",
          }}
        >
          {coupon?.title || "—"}
        </div>
      </div>
    </div>
  </div>
);

const ErrorPanel: React.FC<{ message: string; onClose: () => void }> = ({
  message,
  onClose,
}) => (
  <div
    style={{
      position: "absolute",
      left: 24,
      right: 24,
      top: "calc(env(safe-area-inset-top, 0px) + 280px)",
      zIndex: 5,
      textAlign: "center",
      color: "#fff",
    }}
  >
    <div
      style={{
        background: "rgba(0,0,0,0.18)",
        borderRadius: 18,
        padding: "22px 18px",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
      }}
    >
      <div style={{ fontSize: 32, marginBottom: 8 }}>⚠️</div>
      <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 8 }}>{message}</div>
      <button
        onClick={onClose}
        style={{
          marginTop: 6,
          background: "#fff",
          color: brand,
          border: 0,
          borderRadius: 999,
          padding: "10px 22px",
          fontSize: 13,
          fontWeight: 800,
          cursor: "pointer",
          fontFamily: "inherit",
        }}
      >
        さがすに戻る
      </button>
    </div>
  </div>
);

export default ActiveScreen;
